import { effect, Injectable, signal, Signal } from '@angular/core';

export interface MobileDetectionConfig {
  breakpoint?: number;
  debounceMs?: number;
}

const DEFAULT_MOBILE_BREAKPOINT = 900;
const DEFAULT_DEBOUNCE_MS = 150;

export function createMobileDetectionSignal(config: MobileDetectionConfig = {}): Signal<boolean> {
  const breakpoint = config.breakpoint ?? DEFAULT_MOBILE_BREAKPOINT;
  const debounceMs = config.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  const isMobile = signal(checkIsMobile(breakpoint));

  let timeoutId: number | undefined;
  let isListening = false;

  const handleResize = (): void => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    timeoutId = window.setTimeout(() => {
      isMobile.set(checkIsMobile(breakpoint));
      timeoutId = undefined;
    }, debounceMs);
  };

  effect((onCleanup) => {
    if (!isListening) {
      window.addEventListener('resize', handleResize, { passive: true });
      isListening = true;
    }

    onCleanup(() => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      window.removeEventListener('resize', handleResize);
      isListening = false;
    });
  });

  return isMobile.asReadonly();
}

function checkIsMobile(breakpoint: number): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.innerWidth < breakpoint;
}
@Injectable({ providedIn: 'root' })
export class MobileDetection {
  readonly isMobile: Signal<boolean>;

  constructor() {
    this.isMobile = createMobileDetectionSignal();
  }
}
